import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useGetDashboardQuery } from "../../api/apiSlice";
import { TimePeriodEnum } from "../../utils/types";

const timePeriodOptions = [
  { label: "Last 7 Days", value: TimePeriodEnum.Last7Days },
  { label: "Last Month", value: TimePeriodEnum.LastMonth },
  { label: "Last Year", value: TimePeriodEnum.LastYear },
  { label: "Custom Date Range", value: TimePeriodEnum.Custom },
];

const getDateRange = (period: TimePeriodEnum): { startDate: string; endDate: string } => {
  const today = dayjs();
  const endDate = today.format("YYYY-MM-DD");

  switch (period) {
    case TimePeriodEnum.Last7Days:
      return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
    case TimePeriodEnum.LastMonth:
      return { startDate: today.subtract(30, "day").format("YYYY-MM-DD"), endDate };
    case TimePeriodEnum.LastYear:
      return { startDate: today.subtract(365, "day").format("YYYY-MM-DD"), endDate };
    default:
      return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
  }
};

interface DashboardCard {
  title: string;
  description: string;
  value: number;
}

const DashboardSection: React.FC<{ title: string; subtitle?: string; cards: DashboardCard[] }> = ({ title, subtitle, cards }) => (
  <Box mb={2}>
    <Box mb={1}>
      <Typography variant="subtitle2" color="text.primary" component="span">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" ml={1}>
          — {subtitle}
        </Typography>
      )}
    </Box>
    <Grid container spacing={2}>
      {cards.map(card => (
        <Grid size={{ xs: 12, sm: 6 }} key={card.title}>
          <Card
            sx={{
              height: "100%",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" color="text.primary">
                {card.title}
              </Typography>
            </Box>
            <Divider />
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                {card.value.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

export const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<TimePeriodEnum>(TimePeriodEnum.Last7Days);
  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(dayjs().subtract(7, "day"));
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(dayjs());

  const queryParams = useMemo(() => {
    if (period === TimePeriodEnum.Custom) {
      return {
        startDate: (customStartDate ?? dayjs().subtract(7, "day")).format("YYYY-MM-DD"),
        endDate: (customEndDate ?? dayjs()).format("YYYY-MM-DD"),
      };
    }
    return getDateRange(period);
  }, [period, customStartDate, customEndDate]);

  const { data, isLoading, isFetching, fulfilledTimeStamp, refetch } = useGetDashboardQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const loading = isLoading || isFetching;

  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriod(event.target.value as TimePeriodEnum);
  };

  const periodLabel = useMemo(() => {
    const start = queryParams.startDate;
    const end = queryParams.endDate;
    return `${dayjs(start).format("MMM DD, YYYY")} to ${dayjs(end).format("MMM DD, YYYY")}`;
  }, [queryParams]);

  const lastUpdated = useMemo(() => {
    if (fulfilledTimeStamp) {
      return dayjs(fulfilledTimeStamp).format("MMM DD, YYYY HH:mm:ss");
    }
    return "—";
  }, [fulfilledTimeStamp]);

  const summaryCards = [
    {
      title: "New User Signups",
      description: "Mobile users created within period",
      value: data?.totalNewUsers ?? 0,
    },
    {
      title: "Profile Completions",
      description: "Mobile users created within period who completed onboarding",
      value: data?.totalProfilesCompleted ?? 0,
    },
    {
      title: "Total Published Quotes",
      description: "Total quotes published in the period",
      value: data?.totalPublishedQuotes ?? 0,
    },
    {
      title: "Total Quote Views",
      description: "Quote views recorded within period",
      value: data?.totalQuoteViews ?? 0,
    },
  ];

  const subscriptionActivityCards = [
    {
      title: "First-Time Subscribers",
      description: "Users who subscribed for the first time ever",
      value: data?.totalNewSubscriptions ?? 0,
    },
    {
      title: "Billing Activity",
      description: "Users who started or renewed a subscription in period",
      value: data?.totalNewBillingCycles ?? 0,
    },
    {
      title: "Unsubscribed",
      description: "Users who cancelled or expired in period",
      value: data?.totalUnsubscribed ?? 0,
    },
  ];

  const subscriptionSnapshotCards = [
    {
      title: "Active Subscribers",
      description: "Currently paying subscribers",
      value: data?.totalActiveSubscribers ?? 0,
    },
    {
      title: "Trialing",
      description: "Currently on free trial",
      value: data?.totalTrialing ?? 0,
    },
    {
      title: "Total With Access",
      description: "Active + trialing + grace period",
      value: data?.totalWithAccess ?? 0,
    },
    {
      title: "Churned",
      description: "Expired, paused, or in billing retry",
      value: data?.totalChurned ?? 0,
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%" }}>
        {/* Header Row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
          {/* Left: Time Period selector */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body1" color="text.secondary">
              Time Period:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={period} onChange={handlePeriodChange} displayEmpty>
                {timePeriodOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {period === TimePeriodEnum.Custom && (
              <>
                <DatePicker
                  label="Start Date"
                  value={customStartDate}
                  onChange={newValue => setCustomStartDate(newValue)}
                  maxDate={customEndDate ?? dayjs()}
                  slotProps={{
                    textField: { size: "small", sx: { minWidth: 160 } },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={customEndDate}
                  onChange={newValue => setCustomEndDate(newValue)}
                  minDate={customStartDate ?? undefined}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { size: "small", sx: { minWidth: 160 } },
                  }}
                />
              </>
            )}
          </Box>

          {/* Right: Last Updated, Date Period, Refresh */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              Last Updated: {lastUpdated} &nbsp;|&nbsp; {periodLabel}
            </Typography>
            <Tooltip title="Refresh data">
              <span style={{ display: "inline-flex" }}>
                <IconButton size="small" onClick={refetch} disabled={loading} aria-label="Refresh dashboard data">
                  <RefreshIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {/* Summary Cards */}
        {!loading && (
          <>
            <DashboardSection title="Usage" cards={summaryCards} />
            <DashboardSection title="Subscription Activity" subtitle="within selected period" cards={subscriptionActivityCards} />
            <DashboardSection
              title="Subscription Snapshot"
              subtitle="current totals, independent of date range"
              cards={subscriptionSnapshotCards}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};
